import type { PropsWithChildren } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import type { AnimationPlaybackControls } from 'framer-motion';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';

import ellipseOrbitClasses from './EllipseOrbitAnimation.module.scss';

export type EllipseCrownOrbitAnimationRef = {
  start: (direction?: 'right' | 'left') => Promise<void>;
  reset: () => void;
};

interface IProps {
  shown?: boolean;
  direction?: 'right' | 'left';
  circleWidth?: number;
  circleHeight?: number;
  childrenSize?: number;

  duration?: number;
  bounceIn?: number;
  bounceFurther?: number;
  bounceBack?: number;

  innerClassName?: string;
  wrapperClassName?: string;
}

export const EllipseOrbitAnimation = forwardRef<EllipseCrownOrbitAnimationRef, PropsWithChildren<IProps>>(
  function EllipseOrbitAnimation(
    {
      children,
      shown,
      direction = 'right',
      circleWidth = 30,
      circleHeight = 20,
      childrenSize = 18,
      duration = 500,
      bounceIn = 160,
      bounceFurther = 200,
      bounceBack = 240,
      innerClassName,
      wrapperClassName,
    },
    ref,
  ) {
    const [center, setCenter] = useState({ x: 0, y: 0 });
    const currentAnim = useRef<AnimationPlaybackControls | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const rotationValue = useMotionValue(0);

    const orbitXCoordinate = useTransform(rotationValue, (v) => circleWidth * Math.sin(v));
    const orbitYCoordinate = useTransform(rotationValue, (v) => -circleHeight * Math.cos(v));
    const angleRad = useTransform<number, number>(
      [orbitXCoordinate, orbitYCoordinate],
      ([x, y]) => Math.atan2(-y, -x) + Math.PI * 1.5,
    );
    const angleDeg = useTransform(angleRad, (r) => (r * 180) / Math.PI);

    useLayoutEffect(() => {
      try {
        const el = wrapperRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
          return;
        }
        const ro = new ResizeObserver(() => {
          const rect = el.getBoundingClientRect();
          setCenter({ x: rect.width / 2, y: rect.height / 2 });
        });
        ro.observe(el);
        const rect = el.getBoundingClientRect();
        setCenter({ x: rect.width / 2, y: rect.height / 2 });
        return () => ro.disconnect();
      } catch {
        return () => null;
      }
    }, []);

    const absX = useTransform(orbitXCoordinate, (vx) => center.x + vx - childrenSize / 2);
    const absY = useTransform(orbitYCoordinate, (vy) => center.y + vy - childrenSize / 2);

    const stopAnim = useCallback(() => {
      currentAnim.current?.stop();
      currentAnim.current = null;
    }, []);

    const playSegment = useCallback(
      (to: number, ms: number, ease: any) =>
        new Promise<void>((resolve) => {
          stopAnim();
          currentAnim.current = animate(rotationValue, to, {
            duration: ms / 1000,
            ease,
            onComplete: () => {
              resolve();
            },
          });
        }),
      [rotationValue, stopAnim],
    );

    const play = useCallback(
      async (dir: 'right' | 'left' = 'right') => {
        const d = dir === 'right' ? 1 : -1;
        stopAnim();
        rotationValue.set(0);

        await playSegment(d * Math.PI * 2, duration, 'linear');
        await playSegment(d * (Math.PI * 2 + 0.2), bounceIn, [0.05, 0.9, 0.2, 1]);
        await playSegment(d * (Math.PI * 2 - 0.05), bounceFurther, [0.4, 0.0, 0.2, 1]);
        await playSegment(d * Math.PI * 2, bounceBack, [0.2, 0.8, 0.2, 1]);
      },
      [bounceBack, bounceFurther, bounceIn, duration, playSegment, rotationValue, stopAnim],
    );

    const reset = useCallback(() => {
      stopAnim();
      rotationValue.set(0);
    }, [stopAnim, rotationValue]);

    useImperativeHandle(ref, () => ({ start: play, reset }), [play, reset]);

    useEffect(() => {
      if (shown === undefined) {
        return;
      }
      if (shown) {
        void play(direction);
      } else {
        reset();
      }
    }, [shown, direction, play, reset]);

    return (
      <div ref={wrapperRef} className={classNames(ellipseOrbitClasses.wrapper, wrapperClassName)}>
        <motion.div
          className={ellipseOrbitClasses.motionDiv}
          style={{
            x: absX,
            y: absY,
            rotate: angleDeg,
          }}
        >
          <div
            className={classNames(ellipseOrbitClasses.container, innerClassName)}
            style={{
              width: childrenSize,
              height: childrenSize,
            }}
          >
            {children}
          </div>
        </motion.div>
      </div>
    );
  },
);
